import { useState } from "react";
import { Check, Camera, Sparkles } from "lucide-react";
import { Badge } from "@/components/library/badges";

// ChecklistItem — supports photo & AI requirement flags (metadata-driven).
export function ChecklistItem({ item, checked, onToggle, readOnly, testid }) {
  return (
    <label data-testid={testid || `checklist-item-${item.id}`}
      className={`flex items-center gap-3 py-2 ${readOnly ? "" : "cursor-pointer"} group`}>
      <button type="button" disabled={readOnly} onClick={() => onToggle?.(item.id)}
        className={`h-5 w-5 rounded-md border grid place-items-center transition-colors shrink-0 ${
          checked ? "bg-accent border-accent text-white" : "border-border hover:border-accent"}`}>
        {checked && <Check className="h-3.5 w-3.5" />}
      </button>
      <span className={`text-sm flex-1 ${checked ? "line-through text-muted-foreground" : ""}`}>{item.text}</span>
      {item.requires_photo && <Camera className="h-3.5 w-3.5 text-muted-foreground" title="Kräver foto" />}
      {item.requires_ai && <Sparkles className="h-3.5 w-3.5 text-accent" title="Kräver AI-validering" />}
    </label>
  );
}

// Checklist — composes items, progress bar. State controlled or internal.
export function Checklist({ checklist, readOnly, onChange, testid }) {
  const [local, setLocal] = useState(() => new Set(checklist.items.filter((i) => i.checked).map((i) => i.id)));
  const toggle = (id) => {
    const next = new Set(local);
    next.has(id) ? next.delete(id) : next.add(id);
    setLocal(next);
    onChange?.([...next]);
  };
  const total = checklist.items.length;
  const done = checklist.items.filter((i) => local.has(i.id)).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div data-testid={testid || `checklist-${checklist.id}`} className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-display font-semibold tracking-tight">{checklist.title}</h3>
          {checklist.frequency && <Badge>{checklist.frequency}</Badge>}
        </div>
        <Badge tone={pct === 100 ? "success" : "neutral"}>{done}/{total}</Badge>
      </div>
      <div className="h-1.5 bg-secondary rounded-full mt-4 overflow-hidden">
        <div className="h-full bg-[hsl(145_63%_42%)] transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-3">
        {checklist.items.map((it) => (
          <ChecklistItem key={it.id} item={it} checked={local.has(it.id)} onToggle={toggle} readOnly={readOnly} />
        ))}
      </div>
    </div>
  );
}
